import { useState } from "react";
import ConcentricLoader from "@hudson/ui/components/Loader/ConcentricLoader";
import { FormProvider, useForm } from "react-hook-form";
import Locations from "./Locations";
import useContent from "@lib/use_content";
import userProfileAddPud from "@services/user_profile_add_pud";
import userProfileDeletePud from "@services/user_profile_delete_pud";
import Error from "@hudson/ui/components/Error";
import Success from "@hudson/ui/components/Success";
import Session from "@lib/session";
import AddButton from "./AddPUD/AddButton";
import Field from "./AddPUD/Field";

interface FormProps {
  locations?: be.Location[];
}

export default function Form({ locations = [] }: FormProps) {
  const { data, loading } = useContent([
    "beErrProfileLocationsAdd",
    "beErrProfileLocationsDelete",
    "beProfileLocationsAddButton",
    "beProfileLocationsAddSuccess",
    "beProfileLocationsDeleteSuccess",
    "beProfileLocationsTitle",
  ]);
  const [errorMessage, setErrorMessage] = useState("");
  const [showField, setShowField] = useState(false);
  const [success, setSuccess] = useState("");

  // Form setup
  const formMethods = useForm();
  const [submitting, setSubmitting] = useState(false);

  async function addLocation(): Promise<string> {
    setErrorMessage("");
    setSuccess("");

    const userId = Session.getBooking().profiles.user;
    if (!userId) return;

    const location = formMethods.getValues().location || {};
    if (!location.code) return;
    setSubmitting(true);

    const success = await userProfileAddPud(userId, location);

    if (!success) {
      setErrorMessage(data?.content.beErrProfileLocationsAdd);
    } else {
      formMethods.reset();
      setSuccess(data?.content.beProfileLocationsAddSuccess);
      setShowField(false);
    }

    setSubmitting(false);
  }

  async function removeLocation(id: number): Promise<string> {
    setErrorMessage("");
    setSuccess("");

    const userId = Session.getBooking().profiles.user;
    if (!userId) return;

    setSubmitting(true);

    const success = await userProfileDeletePud(userId, id);

    if (!success) {
      setErrorMessage(data?.content.beErrProfileLocationsDelete);
    } else {
      formMethods.reset();
      setSuccess(data?.content.beProfileLocationsDeleteSuccess);
      setShowField(false);
    }

    setSubmitting(false);
  }

  return loading ? (
    <ConcentricLoader className="opacity-60 max-w-2xl mx-auto px-4 sm:py-10 sm:px-6 lg:px-8" />
  ) : (
    <div className="pb-8 min-h-[500px] mx-auto max-w-[600px]">
      <div className="mt-8 mb-4 ">
        {success && <Success className="mb-6" message={success} heavyStyle scrollTo />}
        {errorMessage && <Error className="mb-6" message={errorMessage} heavyStyle />}

        <FormProvider {...formMethods}>
          <form
            id="profile-add-location"
            onSubmit={(e) => {
              e.preventDefault();
              formMethods.handleSubmit(addLocation)();
            }}
            noValidate
          >
            {showField ? (
              <Field className="max-w-[500px]" onChange={addLocation} submitting={submitting} />
            ) : (
              <AddButton
                label={data?.content.beProfileLocationsAddButton}
                onClick={() => setShowField(true)}
                submitting={submitting}
              />
            )}
          </form>
        </FormProvider>
      </div>

      {locations.length ? (
        <>
          <div className="mt-8 mb-4 space-y-1">
            <h3 className="text-xl leading-6 font-bold text-gray-900">
              {data?.content.beProfileLocationsTitle}
            </h3>
          </div>

          <Locations locations={locations} onRemove={removeLocation} submitting={submitting} />
        </>
      ) : null}
    </div>
  );
}
